import { createSelector } from '@ngrx/store';
import { equipTypeSelectors } from './equipment-type.state';
import { equipModelSelectors } from './equipment-model.state';
import { equipMachineSelectors } from './equipment-machine.state';
import {
  EquipmentMachine,
  EquipmentOptionType,
  EquipmentType,
  EquipmentTypeVM,
} from '@app/models';
import { equipOptionSelectors } from './equipment-option.state';
import { equipOptTypeSelectors } from './equipment-option-type.state';

export interface OptionSet {
  allOptions: EquipmentOptionType[];
  optionSets: number[][];
}

export const equipTypesByClass = createSelector(
  equipTypeSelectors.selectAll,
  equipTypes => {
    const res: { [key: number]: EquipmentType[] } = {};
    equipTypes.forEach(eqType => {
      if (!(eqType.equipClass in res)) {
        res[eqType.equipClass] = [];
      }
      res[eqType.equipClass].push(eqType);
    });
    return res;
  }
);

export const currentEquipTypeModels = createSelector(
  equipTypeSelectors.selectCurrentEntityKey,
  equipModelSelectors.selectAll,
  (currentTypeId, equipModels) => {
    if (!currentTypeId) {
      return [];
    }
    return equipModels.filter(em => em.equipType === currentTypeId);
  }
);

export const machinesByType = createSelector(
  equipTypeSelectors.selectAll,
  equipModelSelectors.selectEntities,
  equipMachineSelectors.selectAll,
  (equipTypes, equipModels, equipMachines) => {
    const res: { [key: number]: EquipmentMachine[] } = {};
    for (const equipType of equipTypes) {
      res[equipType.id] = equipMachines.filter(machine => {
        if (!machine.rental) {
          return equipModels[machine.makeModel].equipType === equipType.id;
        } else {
          return machine.rentalType === equipType.id;
        }
      });
    }
    return res;
  }
);

export const optionSetsByType = createSelector(
  machinesByType,
  equipOptionSelectors.selectAll,
  equipOptTypeSelectors.selectEntities,
  (mbt, equipOptions, optionTypes) => {
    const res: { [key: number]: OptionSet } = {};
    for (const typeId of Object.keys(mbt)) {
      const allOptions: EquipmentOptionType[] = [];
      const optionSets: number[][] = [];
      for (const machine of mbt[typeId]) {
        const opts = equipOptions.filter(opt => opt.equipment === machine.id);
        if (opts.length > 0) {
          const optTypes: EquipmentOptionType[] = opts
            .filter(opt => opt.optionType in optionTypes)
            .map(opt => optionTypes[opt.optionType]);
          const optSet = optTypes.map(optType => optType.id).sort();
          optTypes.forEach(optType => {
            if (!allOptions.find(aoType => aoType.id === optType.id)) {
              allOptions.push(optType);
            }
          });
          if (
            !optionSets.find(
              os =>
                os.length === optSet.length &&
                os.every(osv => optSet.includes(osv))
            )
          ) {
            optionSets.push(optSet);
          }
        }
      }
      res[typeId] = {
        allOptions,
        optionSets,
      };
    }
    return res;
  }
);

export const selectEquipTypesVM = createSelector(
  equipTypeSelectors.selectAll,
  optionSetsByType,
  (equipTypes, optionSets): EquipmentTypeVM[] => {
    return equipTypes.map(eqType => ({
      ...eqType,
      availableOptions: eqType.id in optionSets ? optionSets[eqType.id] : null,
    }));
  }
);
export const selectEquipTypesVMEntities = createSelector(
  selectEquipTypesVM,
  (equipTypes): { [key: number]: EquipmentTypeVM } => {
    const payload: { [key: number]: EquipmentTypeVM } = {};
    equipTypes.forEach(eqType => (payload[eqType.id] = eqType));
    return payload;
  }
);
